import React, { useState, useEffect, useRef, useCallback } from 'react';
import './DocAnnotationLayer.css';

/**
 * DocAnnotationLayer - Overlay for drawing/highlighting on document pages
 * 
 * Props:
 * - documentId: ID of the current document
 * - pageNumber: Current page number
 * - roomId: Optional room ID for room-scoped annotations
 * - annotations: Array of existing annotations for this doc/page
 * - isPresenter: Only presenter can add annotations
 * - onAddAnnotation: Callback when a new annotation is added
 * - onDeleteAnnotation: Callback to delete an annotation
 * - onUpdateAnnotation: Callback to update an annotation's data (e.g. drag)
 * - activeTool: 'pointer' | 'highlight' | 'pen' | 'text' | 'eraser' | 'line' | 'rect' | 'circle' | 'select' | null
 * - toolColor: Current drawing color
 * - toolSize: Stroke width
 * - shapeSize: Stroke width for shapes
 * - fillShapes: Fill shapes with color
 * - supabase: Supabase client instance
 * - userId: Current user ID
 */
const DocAnnotationLayer = ({
  documentId,
  pageNumber = 1,
  roomId,
  annotations = [],
  isPresenter = false,
  onAddAnnotation,
  onDeleteAnnotation,
  onUpdateAnnotation,
  activeTool = null,
  toolColor = '#ff0000',
  toolSize = 3,
  shapeSize = 3,
  fillShapes = false,
  supabase,
  userId,
  rotation = 0,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [pointerPos, setPointerPos] = useState(null);
  const [remotePointers, setRemotePointers] = useState({}); // other users' pointers
  const pointerChannelRef = useRef(null);
  const isPointerDownRef = useRef(false); // audience click+hold pointer broadcast
  
  // Shape drawing state
  const [shapeStart, setShapeStart] = useState(null); // {x, y} normalized start point
  const [shapeEnd, setShapeEnd] = useState(null); // {x, y} normalized end point
  
  // Eraser path state (stores eraser strokes like whiteboard)
  const [eraserPath, setEraserPath] = useState([]);
  
  // Text input state (mirrors whiteboard's textBox)
  const [textBox, setTextBox] = useState({ active: false, x: 0.1, y: 0.1, text: '', color: '#ff0000' });
  const textDragging = useRef(false);
  const textDragOffset = useRef({ dx: 0, dy: 0 });

  // Selection state for dragging existing annotations (shapes/text)
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const dragAnnotationRef = useRef(null); // { annotation, startCoords: {x,y}, delta: {dx,dy} }

  // Get normalized coordinates (0-1) from mouse event
  const mapDisplayToStored = useCallback((pt) => {
    if (!pt) return null;
    if (rotation !== 90) return pt;
    // Display is rotated 90deg clockwise relative to stored base.
    // stored.x = display.y
    // stored.y = 1 - display.x
    return {
      x: pt.y,
      y: 1 - pt.x,
    };
  }, [rotation]);

  const mapStoredToDisplay = useCallback((pt) => {
    if (!pt) return null;
    if (rotation !== 90) return pt;
    // Inverse mapping of mapDisplayToStored
    // display.x = 1 - stored.y
    // display.y = stored.x
    return {
      x: 1 - pt.y,
      y: pt.x,
    };
  }, [rotation]);

  const getNormalizedCoords = useCallback((e) => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const displayPt = {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
    return mapDisplayToStored(displayPt);
  }, [mapDisplayToStored]);

  // Draw all annotations on canvas
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    const { width, height } = container.getBoundingClientRect();
    
    // Set canvas size to match container
    canvas.width = width;
    canvas.height = height;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw existing annotations (order matters - eraser strokes cut through previous)
    annotations.forEach((ann) => {
      // Set composite operation based on annotation type
      if (ann.annotation_type === 'eraser') {
        ctx.globalCompositeOperation = 'destination-out';
      } else {
        ctx.globalCompositeOperation = 'source-over';
      }
      
      if ((ann.annotation_type === 'drawing' || ann.annotation_type === 'eraser') && ann.data?.paths) {
        ctx.strokeStyle = ann.annotation_type === 'eraser' ? 'rgba(0,0,0,1)' : (ann.data.color || '#ff0000');
        ctx.lineWidth = (ann.data.strokeWidth || 2) * (width / 600); // scale stroke
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        ann.data.paths.forEach((path) => {
          if (path.length < 2) return;
          const first = mapStoredToDisplay(path[0]);
          ctx.beginPath();
          ctx.moveTo(first.x * width, first.y * height);
          path.slice(1).forEach((point) => {
            const p = mapStoredToDisplay(point);
            ctx.lineTo(p.x * width, p.y * height);
          });
          ctx.stroke();
        });
      }

      if (ann.annotation_type === 'highlight' && ann.data) {
        const tl = mapStoredToDisplay({ x: ann.data.x || 0, y: ann.data.y || 0 });
        const br = mapStoredToDisplay({ x: (ann.data.x || 0) + (ann.data.w || 0), y: (ann.data.y || 0) + (ann.data.h || 0) });
        ctx.fillStyle = ann.data.color || 'rgba(255, 255, 0, 0.3)';
        ctx.fillRect(
          Math.min(tl.x, br.x) * width,
          Math.min(tl.y, br.y) * height,
          Math.abs(br.x - tl.x) * width,
          Math.abs(br.y - tl.y) * height
        );
      }

      if (ann.annotation_type === 'text' && ann.data) {
        ctx.fillStyle = ann.data.color || '#333';
        ctx.font = `${(ann.data.fontSize || 14) * (width / 600)}px sans-serif`;
        ctx.textBaseline = 'top'; // Align text from top to match textbox position
        const p = mapStoredToDisplay({ x: ann.data.x || 0, y: ann.data.y || 0 });
        ctx.fillText(ann.data.text, p.x * width, p.y * height);
      }

      // Render shapes (line, rect, square, circle, arrow)
      if (ann.annotation_type === 'shape' && ann.data) {
        const { shapeType, x0, y0, x1, y1, color, strokeWidth, fill } = ann.data;
        ctx.strokeStyle = color || '#ff0000';
        ctx.lineWidth = (strokeWidth || shapeSize || 2) * (width / 600);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        const p0 = mapStoredToDisplay({ x: x0, y: y0 });
        const p1 = mapStoredToDisplay({ x: x1, y: y1 });
        const px0 = p0.x * width, py0 = p0.y * height;
        const px1 = p1.x * width, py1 = p1.y * height;
        
        if (shapeType === 'line' || shapeType === 'arrow') {
          ctx.beginPath();
          ctx.moveTo(px0, py0);
          ctx.lineTo(px1, py1);
          ctx.stroke();

          if (shapeType === 'arrow') {
            const headLen = Math.max(10, ctx.lineWidth * 3);
            const ang = Math.atan2(py1 - py0, px1 - px0);
            ctx.beginPath();
            ctx.moveTo(px1, py1);
            ctx.lineTo(
              px1 - headLen * Math.cos(ang - Math.PI / 6),
              py1 - headLen * Math.sin(ang - Math.PI / 6)
            );
            ctx.moveTo(px1, py1);
            ctx.lineTo(
              px1 - headLen * Math.cos(ang + Math.PI / 6),
              py1 - headLen * Math.sin(ang + Math.PI / 6)
            );
            ctx.stroke();
          }
        } else if (shapeType === 'rect' || shapeType === 'square') {
          if (fill) {
            ctx.fillStyle = color || '#ff0000';
            ctx.fillRect(px0, py0, px1 - px0, py1 - py0);
          } else {
            ctx.strokeRect(px0, py0, px1 - px0, py1 - py0);
          }
        } else if (shapeType === 'circle') {
          const cx = (px0 + px1) / 2;
          const cy = (py0 + py1) / 2;
          const rx = Math.abs(px1 - px0) / 2;
          const ry = Math.abs(py1 - py0) / 2;
          ctx.beginPath();
          ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
          if (fill) {
            ctx.fillStyle = color || '#ff0000';
            ctx.fill();
          } else {
            ctx.stroke();
          }
        }
      }
    });

    // Reset composite operation
    ctx.globalCompositeOperation = 'source-over';
    
    // Draw current path while drawing (pen)
    if (currentPath.length > 1) {
      ctx.strokeStyle = toolColor;
      ctx.lineWidth = toolSize * (width / 600);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const first = mapStoredToDisplay(currentPath[0]);
      ctx.beginPath();
      ctx.moveTo(first.x * width, first.y * height);
      currentPath.slice(1).forEach((point) => {
        const p = mapStoredToDisplay(point);
        ctx.lineTo(p.x * width, p.y * height);
      });
      ctx.stroke();
    }
    
    // Draw current eraser path while erasing
    if (eraserPath.length > 1) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = toolSize * (width / 600);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const first = mapStoredToDisplay(eraserPath[0]);
      ctx.beginPath();
      ctx.moveTo(first.x * width, first.y * height);
      eraserPath.slice(1).forEach((point) => {
        const p = mapStoredToDisplay(point);
        ctx.lineTo(p.x * width, p.y * height);
      });
      ctx.stroke();
      ctx.globalCompositeOperation = 'source-over';
    }

    // Draw shape preview while drawing
    if (shapeStart && shapeEnd && ['line', 'rect', 'square', 'circle', 'arrow'].includes(activeTool)) {
      ctx.strokeStyle = toolColor;
      ctx.lineWidth = shapeSize * (width / 600);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const p0 = mapStoredToDisplay(shapeStart);
      const p1d = mapStoredToDisplay(shapeEnd);
      const px0 = p0.x * width, py0 = p0.y * height;
      let px1 = p1d.x * width, py1 = p1d.y * height;

      if (activeTool === 'square') {
        const w = px1 - px0;
        const h = py1 - py0;
        const s = Math.max(Math.abs(w), Math.abs(h)) || 0;
        const signX = Math.sign(w || 1);
        const signY = Math.sign(h || 1);
        px1 = px0 + signX * s;
        py1 = py0 + signY * s;
      }

      if (activeTool === 'line' || activeTool === 'arrow') {
        ctx.beginPath();
        ctx.moveTo(px0, py0);
        ctx.lineTo(px1, py1);
        ctx.stroke();
        if (activeTool === 'arrow') {
          const headLen = Math.max(10, ctx.lineWidth * 3);
          const ang = Math.atan2(py1 - py0, px1 - px0);
          ctx.beginPath();
          ctx.moveTo(px1, py1);
          ctx.lineTo(
            px1 - headLen * Math.cos(ang - Math.PI / 6),
            py1 - headLen * Math.sin(ang - Math.PI / 6)
          );
          ctx.moveTo(px1, py1);
          ctx.lineTo(
            px1 - headLen * Math.cos(ang + Math.PI / 6),
            py1 - headLen * Math.sin(ang + Math.PI / 6)
          );
          ctx.stroke();
        }
      } else if (activeTool === 'rect') {
        if (fillShapes) {
          ctx.fillStyle = toolColor;
          ctx.fillRect(px0, py0, px1 - px0, py1 - py0);
        } else {
          ctx.strokeRect(px0, py0, px1 - px0, py1 - py0);
        }
      } else if (activeTool === 'circle') {
        const cx = (px0 + px1) / 2;
        const cy = (py0 + py1) / 2;
        const rx = Math.abs(px1 - px0) / 2;
        const ry = Math.abs(py1 - py0) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        if (fillShapes) {
          ctx.fillStyle = toolColor;
          ctx.fill();
        } else {
          ctx.stroke();
        }
      }
    }

  }, [annotations, currentPath, eraserPath, activeTool, toolColor, toolSize, shapeStart, shapeEnd, fillShapes, mapStoredToDisplay]);

  // Redraw on any change
  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      redrawCanvas();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [redrawCanvas]);

  // Eraser now stores strokes like whiteboard (no longer deletes whole objects)

  // Pointer realtime sharing (Supabase broadcast, parallels whiteboard)
  useEffect(() => {
    if (!roomId || !documentId || !supabase) return;

    const channel = supabase
      .channel(`doc-pointers-${roomId}-${documentId}`)
      .on('broadcast', { event: 'pointer' }, (payload) => {
        const data = payload?.payload || {};
        const senderId = data.userId;
        if (!senderId || senderId === userId) return;

        setRemotePointers(prev => {
          if (!data.active) {
            const next = { ...prev };
            delete next[senderId];
            return next;
          }
          return {
            ...prev,
            [senderId]: { x: data.x, y: data.y, isPresenter: !!data.isPresenter },
          };
        });
      })
      .subscribe();

    pointerChannelRef.current = channel;

    return () => {
      pointerChannelRef.current = null;
      setRemotePointers({});
      supabase.removeChannel(channel);
    };
  }, [roomId, documentId, supabase, userId]);

  const broadcastPointer = (coords, active) => {
    const channel = pointerChannelRef.current;
    if (!channel || !roomId || !userId || !coords) return;
    channel.send({
      type: 'broadcast',
      event: 'pointer',
      payload: {
        x: coords.x,
        y: coords.y,
        active,
        userId,
        isPresenter,
      },
    });
  };

  // Hit-test helper for shapes and text in normalized space
  const findSelectableAt = (nx, ny, tolerance = 0.02) => {
    for (let i = annotations.length - 1; i >= 0; i--) {
      const ann = annotations[i];
      if (!ann || !ann.data) continue;

      if (ann.annotation_type === 'text') {
        const { x, y, text = '', fontSize = 14 } = ann.data;
        const textWidth = (text.length * fontSize * 0.6) / 600; // normalized
        const textHeight = (fontSize * 1.2) / 600;
        const minX = x - tolerance;
        const maxX = x + textWidth + tolerance;
        const minY = y - tolerance;
        const maxY = y + textHeight + tolerance;
        if (nx >= minX && nx <= maxX && ny >= minY && ny <= maxY) return ann;
        continue;
      }

      if (ann.annotation_type === 'shape') {
        const { shapeType, x0, y0, x1, y1 } = ann.data;
        if (shapeType === 'line' || shapeType === 'arrow') {
          const dx = x1 - x0; const dy = y1 - y0;
          const lenSq = dx * dx + dy * dy;
          if (lenSq === 0) continue;
          let t = ((nx - x0) * dx + (ny - y0) * dy) / lenSq;
          t = Math.max(0, Math.min(1, t));
          const px = x0 + t * dx;
          const py = y0 + t * dy;
          const dist = Math.hypot(nx - px, ny - py);
          if (dist <= tolerance) return ann;
        } else if (shapeType === 'rect' || shapeType === 'square') {
          const minX = Math.min(x0, x1) - tolerance;
          const maxX = Math.max(x0, x1) + tolerance;
          const minY = Math.min(y0, y1) - tolerance;
          const maxY = Math.max(y0, y1) + tolerance;
          if (nx >= minX && nx <= maxX && ny >= minY && ny <= maxY) return ann;
        } else if (shapeType === 'circle') {
          const cx = (x0 + x1) / 2;
          const cy = (y0 + y1) / 2;
          const r = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) / 2;
          const dist = Math.hypot(nx - cx, ny - cy);
          if (Math.abs(dist - r) <= tolerance || dist <= r + tolerance) return ann;
        }
      }
    }
    return null;
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    const coords = getNormalizedCoords(e);
    if (!coords) return;

    // Pointer click+hold for audience and explicit pointer tool for presenter
    if (!isPresenter || activeTool === 'pointer') {
      setPointerPos(coords);
      isPointerDownRef.current = true;
      broadcastPointer(coords, true);
      if (!isPresenter) {
        // Audience only has pointer interactions
        return;
      }
    }

    if (!isPresenter || !activeTool) return;

    if (activeTool === 'pen') {
      setIsDrawing(true);
      setCurrentPath([coords]);
    } else if (activeTool === 'eraser') {
      setIsDrawing(true);
      setEraserPath([coords]);
    } else if (['line', 'rect', 'square', 'circle', 'arrow'].includes(activeTool)) {
      setShapeStart(coords);
      setShapeEnd(coords);
      setIsDrawing(true);
    } else if (activeTool === 'text') {
      setTextBox({ active: true, x: coords.x, y: coords.y, text: '', color: toolColor });
    } else if (activeTool === 'select') {
      const hit = findSelectableAt(coords.x, coords.y);
      if (hit) {
        setSelectedAnnotationId(hit.id);
        dragAnnotationRef.current = {
          annotation: hit,
          startCoords: coords,
          delta: { dx: 0, dy: 0 },
        };
        setIsDrawing(true);
      } else {
        setSelectedAnnotationId(null);
        dragAnnotationRef.current = null;
      }
    }
  };

  const handleMouseMove = (e) => {
    const coords = getNormalizedCoords(e);
    if (!coords) return;

    const isPointerMode = (activeTool === 'pointer') || !isPresenter;
    if (isPointerMode) {
      setPointerPos(coords);
      if (activeTool === 'pointer' || isPointerDownRef.current) {
        broadcastPointer(coords, true);
      }
      // Audience/pointer tool do not draw
      if (!isPresenter) return;
    }

    if (!isPresenter || !activeTool) return;

    if (activeTool === 'pen' && isDrawing) {
      setCurrentPath((prev) => [...prev, coords]);
    } else if (['line', 'rect', 'square', 'circle', 'arrow'].includes(activeTool) && isDrawing) {
      setShapeEnd(coords);
    } else if (activeTool === 'eraser' && isDrawing) {
      setEraserPath((prev) => [...prev, coords]);
    } else if (activeTool === 'select' && isDrawing && dragAnnotationRef.current && dragAnnotationRef.current.annotation) {
      const { annotation, startCoords } = dragAnnotationRef.current;
      const dx = coords.x - startCoords.x;
      const dy = coords.y - startCoords.y;
      dragAnnotationRef.current = { ...dragAnnotationRef.current, delta: { dx, dy } };

      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      const ctx = canvas.getContext('2d');
      const { width, height } = container.getBoundingClientRect();

      redrawCanvas();

      if (annotation.annotation_type === 'text') {
        const { x, y, text, color, fontSize } = annotation.data;
        const nx = x + dx;
        const ny = y + dy;
        ctx.fillStyle = color || '#333';
        ctx.font = `${(fontSize || 14) * (width / 600)}px sans-serif`;
        ctx.textBaseline = 'top';
        ctx.fillText(text, nx * width, ny * height);
      } else if (annotation.annotation_type === 'shape') {
        const { shapeType, x0, y0, x1, y1, color, strokeWidth, fill } = annotation.data;
        const px0 = (x0 + dx) * width;
        const py0 = (y0 + dy) * height;
        const px1 = (x1 + dx) * width;
        const py1 = (y1 + dy) * height;
        ctx.strokeStyle = color || '#ff0000';
        ctx.lineWidth = (strokeWidth || shapeSize || 2) * (width / 600);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        if (shapeType === 'line' || shapeType === 'arrow') {
          ctx.beginPath();
          ctx.moveTo(px0, py0);
          ctx.lineTo(px1, py1);
          ctx.stroke();

          if (shapeType === 'arrow') {
            const headLen = Math.max(10, ctx.lineWidth * 3);
            const ang = Math.atan2(py1 - py0, px1 - px0);
            ctx.beginPath();
            ctx.moveTo(px1, py1);
            ctx.lineTo(
              px1 - headLen * Math.cos(ang - Math.PI / 6),
              py1 - headLen * Math.sin(ang - Math.PI / 6)
            );
            ctx.moveTo(px1, py1);
            ctx.lineTo(
              px1 - headLen * Math.cos(ang + Math.PI / 6),
              py1 - headLen * Math.sin(ang + Math.PI / 6)
            );
            ctx.stroke();
          }
        } else if (shapeType === 'rect' || shapeType === 'square') {
          if (fill) {
            ctx.fillStyle = color || '#ff0000';
            ctx.fillRect(px0, py0, px1 - px0, py1 - py0);
          } else {
            ctx.strokeRect(px0, py0, px1 - px0, py1 - py0);
          }
        } else if (shapeType === 'circle') {
          const cx = (px0 + px1) / 2;
          const cy = (py0 + py1) / 2;
          const rx = Math.abs(px1 - px0) / 2;
          const ry = Math.abs(py1 - py0) / 2;
          ctx.beginPath();
          ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
          if (fill) {
            ctx.fillStyle = color || '#ff0000';
            ctx.fill();
          } else {
            ctx.stroke();
          }
        }
      }
    }
  };

  const handleMouseUp = () => {
    // Stop any pointer broadcast on mouse/touch release
    if (isPointerDownRef.current) {
      isPointerDownRef.current = false;
      if (pointerPos) {
        broadcastPointer(pointerPos, false);
      }
    }

    if (!isPresenter) return;

    if (activeTool === 'pen' && currentPath.length > 1 && onAddAnnotation) {
      onAddAnnotation({
        document_id: documentId,
        room_id: roomId,
        page_number: pageNumber,
        annotation_type: 'drawing',
        data: {
          paths: [currentPath],
          color: toolColor,
          strokeWidth: toolSize,
        },
        is_persistent: true,
      });
    }
    
    // Save eraser stroke (like whiteboard - stores eraser path instead of deleting)
    if (activeTool === 'eraser' && eraserPath.length > 1 && onAddAnnotation) {
      onAddAnnotation({
        document_id: documentId,
        room_id: roomId,
        page_number: pageNumber,
        annotation_type: 'eraser',
        data: {
          paths: [eraserPath],
          strokeWidth: toolSize,
        },
        is_persistent: true,
      });
    }

    // Save shape
    if (['line', 'rect', 'square', 'circle', 'arrow'].includes(activeTool) && shapeStart && shapeEnd && onAddAnnotation) {
      onAddAnnotation({
        document_id: documentId,
        room_id: roomId,
        page_number: pageNumber,
        annotation_type: 'shape',
        data: {
          shapeType: activeTool,
          x0: shapeStart.x,
          y0: shapeStart.y,
          x1: shapeEnd.x,
          y1: shapeEnd.y,
          color: toolColor,
          strokeWidth: shapeSize,
          fill: fillShapes,
        },
        is_persistent: true,
      });
    }

    // Finalize select drag
    if (activeTool === 'select' && dragAnnotationRef.current && dragAnnotationRef.current.annotation && onUpdateAnnotation) {
      const { annotation, delta } = dragAnnotationRef.current;
      const { dx, dy } = delta || { dx: 0, dy: 0 };
      if (dx || dy) {
        let newData = null;
        if (annotation.annotation_type === 'text') {
          const { x, y, text, color, fontSize } = annotation.data;
          newData = {
            text,
            x: x + dx,
            y: y + dy,
            color,
            fontSize,
          };
        } else if (annotation.annotation_type === 'shape') {
          const { shapeType, x0, y0, x1, y1, color, strokeWidth, fill } = annotation.data;
          newData = {
            shapeType,
            x0: x0 + dx,
            y0: y0 + dy,
            x1: x1 + dx,
            y1: y1 + dy,
            color,
            strokeWidth,
            fill,
          };
        }
        if (newData) {
          onUpdateAnnotation(annotation.id, newData);
        }
      }
      dragAnnotationRef.current = null;
    }

    setIsDrawing(false);
    setCurrentPath([]);
    setEraserPath([]);
    setShapeStart(null);
    setShapeEnd(null);
  };

  // Handle text submission (mirrors whiteboard)
  const handleTextSubmit = () => {
    if (textBox.text.trim() && onAddAnnotation) {
      onAddAnnotation({
        document_id: documentId,
        room_id: roomId,
        page_number: pageNumber,
        annotation_type: 'text',
        data: {
          text: textBox.text,
          x: textBox.x,
          y: textBox.y,
          color: textBox.color,
          fontSize: 14 + toolSize * 2,
        },
        is_persistent: true,
      });
    }
    setTextBox({ active: false, x: 0.1, y: 0.1, text: '', color: toolColor });
  };

  // Handle text box dragging
  const handleTextDragStart = (e) => {
    textDragging.current = true;
    const rect = e.currentTarget.getBoundingClientRect();
    textDragOffset.current = { 
      dx: e.clientX - rect.left, 
      dy: e.clientY - rect.top 
    };
    e.stopPropagation();
  };

  const handleTextDragMove = (e) => {
    if (!textDragging.current || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const newX = (e.clientX - containerRect.left - textDragOffset.current.dx) / containerRect.width;
    const newY = (e.clientY - containerRect.top - textDragOffset.current.dy) / containerRect.height;
    setTextBox(tb => ({ 
      ...tb, 
      x: Math.max(0, Math.min(0.9, newX)), 
      y: Math.max(0, Math.min(0.9, newY)) 
    }));
  };

  const handleTextDragEnd = () => {
    textDragging.current = false;
  };

  const handleMouseLeave = () => {
    if (activeTool === 'pointer' || !isPresenter) {
      setPointerPos(null);
      if (isPointerDownRef.current) {
        isPointerDownRef.current = false;
        if (pointerPos) {
          broadcastPointer(pointerPos, false);
        }
      }
    }
    if (isDrawing) {
      handleMouseUp();
    }
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      handleMouseDown({ clientX: touch.clientX, clientY: touch.clientY });
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
    }
  };

  const handleTouchEnd = () => {
    handleMouseUp();
  };

  // Get container dimensions for positioning text input
  const containerRect = containerRef.current?.getBoundingClientRect();
  const cw = containerRect?.width || 600;
  const ch = containerRect?.height || 400;

  return (
    <div
      ref={containerRef}
      className={`doc-annotation-layer ${((isPresenter && activeTool) || !isPresenter) ? 'active-tool' : ''} ${activeTool ? `tool-${activeTool}` : ''}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <canvas ref={canvasRef} className="doc-annotation-canvas" />
      {/* Selection highlight around currently selected annotation (shapes/text) */}
      {selectedAnnotationId && (() => {
        const ann = annotations.find(a => a.id === selectedAnnotationId);
        if (!ann || !ann.data) return null;
        let bounds = null;
        if (ann.annotation_type === 'text') {
          const { x, y, text = '', fontSize = 14 } = ann.data;
          const wNorm = (text.length * fontSize * 0.6) / 600;
          const hNorm = (fontSize * 1.2) / 600;
          bounds = { x, y, w: wNorm, h: hNorm };
        } else if (ann.annotation_type === 'shape') {
          const { x0, y0, x1, y1 } = ann.data;
          bounds = {
            x: Math.min(x0, x1),
            y: Math.min(y0, y1),
            w: Math.abs(x1 - x0),
            h: Math.abs(y1 - y0),
          };
        }
        if (!bounds) return null;
        const tl = mapStoredToDisplay({ x: bounds.x, y: bounds.y });
        const br = mapStoredToDisplay({ x: bounds.x + bounds.w, y: bounds.y + bounds.h });
        const left = Math.min(tl.x, br.x);
        const top = Math.min(tl.y, br.y);
        const w = Math.abs(br.x - tl.x);
        const h = Math.abs(br.y - tl.y);
        const pad = 4;
        return (
          <div
            className="wb-selection-highlight"
            style={{
              left: left * cw - pad,
              top: top * ch - pad,
              width: w * cw + pad * 2,
              height: h * ch + pad * 2,
            }}
          />
        );
      })()}

      {/* Local pointer indicator (presenter pointer tool or audience hover) */}
      {(((activeTool === 'pointer') && isPresenter) || !isPresenter) && pointerPos && (() => {
        const isAudienceLocal = !isPresenter;
        const isAudienceActive = isAudienceLocal && isPointerDownRef.current;
        const size = 18;
        const radiusOffset = size / 2;
        const borderColor = isAudienceLocal
          ? (isAudienceActive ? 'rgba(239,68,68,0.95)' : 'rgba(239,68,68,0.6)')
          : 'rgba(239,68,68,0.9)';
        const background = isAudienceLocal && !isAudienceActive
          ? 'transparent'
          : 'rgba(239,68,68,0.08)';
        const p = mapStoredToDisplay(pointerPos);
        return (
          <div
            className="wb-pointer-indicator"
            style={{
              position: 'absolute',
              left: p.x * cw - radiusOffset,
              top: p.y * ch - radiusOffset,
              width: size,
              height: size,
              borderRadius: '999px',
              border: `2px solid ${borderColor}`,
              background,
              boxShadow: 'none',
              pointerEvents: 'none',
              zIndex: 12,
              animation: 'none',
            }}
          />
        );
      })()}

      {/* Remote pointers (presenter blue, audience cyan) */}
      {Object.entries(remotePointers).map(([uid, p]) => {
        const pd = mapStoredToDisplay(p);
        return (
        <div
          key={uid}
          className="wb-pointer-indicator"
          style={{
            position: 'absolute',
            left: pd.x * cw - 10,
            top: pd.y * ch - 10,
            width: 20,
            height: 20,
            borderRadius: '999px',
            background: p.isPresenter
              ? 'radial-gradient(circle, rgba(59,130,246,0.95) 0%, rgba(59,130,246,0.4) 70%, transparent 100%)'
              : 'radial-gradient(circle, rgba(14,165,233,0.95) 0%, rgba(14,165,233,0.4) 70%, transparent 100%)',
            boxShadow: p.isPresenter
              ? '0 0 18px rgba(59,130,246,0.9)'
              : '0 0 14px rgba(14,165,233,0.8)',
            pointerEvents: 'none',
            zIndex: 11,
          }}
        />
      );
      })}
      
      {/* Text box overlay (mirrors whiteboard's draggable textbox) */}
      {textBox.active && (
        (() => {
          const p = mapStoredToDisplay({ x: textBox.x, y: textBox.y });
          return (
        <div
          className="doc-annotation-textbox"
          style={{
            position: 'absolute',
            left: p.x * cw,
            top: p.y * ch,
            background: '#fff',
            border: '1px solid rgba(0,0,0,0.2)',
            borderRadius: 6,
            padding: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            minWidth: 160,
            zIndex: 100,
            cursor: 'move',
          }}
          onMouseDown={handleTextDragStart}
          onMouseMove={handleTextDragMove}
          onMouseUp={handleTextDragEnd}
          onMouseLeave={handleTextDragEnd}
        >
          <textarea
            autoFocus
            value={textBox.text}
            onChange={(e) => setTextBox(tb => ({ ...tb, text: e.target.value }))}
            onMouseDown={(e) => e.stopPropagation()}
            placeholder="Type here..."
            style={{
              width: 200,
              minHeight: 60,
              border: 'none',
              outline: 'none',
              resize: 'both',
              fontSize: 14 + toolSize * 2,
              color: textBox.color,
              background: 'transparent',
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="color"
                value={textBox.color}
                onChange={(e) => setTextBox(tb => ({ ...tb, color: e.target.value }))}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ width: 24, height: 24, border: 'none', padding: 0, cursor: 'pointer' }}
              />
            </label>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => setTextBox({ active: false, x: 0.1, y: 0.1, text: '', color: toolColor })}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                padding: '4px 8px',
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              ✕
            </button>
            <button
              onClick={handleTextSubmit}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                padding: '4px 8px',
                background: '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              ✓
            </button>
          </div>
        </div>
          );
        })()
      )}
    </div>
  );
};

export default DocAnnotationLayer;
